// src/components/admin/RatingJobsList.js
import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import toast from 'react-hot-toast';

const RatingJobsList = ({ onJobSelect }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [processingJobs, setProcessingJobs] = useState(new Set());

  useEffect(() => {
    fetchJobs(currentPage);
  }, [currentPage]);

  const fetchJobs = async (page = 1) => {
    try {
      setLoading(true);
      const response = await apiService.getRatingJobs(page, 10);
      setJobs(response.data.jobs);
      setPagination(response.data.pagination);
    } catch (err) {
      setError('Failed to fetch rating jobs');
      console.error('Fetch jobs failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const handleJobClick = async (job) => {
    if (job.status === 'completed') {
      try {
        const results = await apiService.getRatingJobResults(job.id);
        onJobSelect(job, results.data);
      } catch (err) {
        console.error('Failed to fetch job results:', err);
      }
    } else {
      onJobSelect(job, null);
    }
  };

  const handleCancelJob = async (e, jobId) => {
    e.stopPropagation(); // Prevent triggering handleJobClick
    
    setProcessingJobs(prev => new Set([...prev, jobId]));
    try {
      await apiService.cancelRatingJob(jobId);
      toast.success('Job cancelled successfully');
      
      // Refresh the jobs list
      await fetchJobs(currentPage);
    } catch (error) {
      toast.error(error.message || 'Failed to cancel job');
    } finally {
      setProcessingJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  const handleRetryJob = async (e, jobId) => {
    e.stopPropagation(); // Prevent triggering handleJobClick
    
    setProcessingJobs(prev => new Set([...prev, jobId]));
    try {
      const result = await apiService.retryRatingJobSync(jobId);
      toast.success(`Job retried successfully! ${result.sync_result?.data?.successful || 0} resumes processed.`);
      
      // Refresh the jobs list
      await fetchJobs(currentPage);
    } catch (error) {
      toast.error(error.message || 'Failed to retry job');
    } finally {
      setProcessingJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  if (loading && jobs.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
        <button
          onClick={() => fetchJobs(currentPage)}
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Rating Jobs History</h3>
      </div>

      <div className="divide-y divide-gray-200">
        {jobs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No rating jobs found
          </div>
        ) : (
          jobs.map((job) => (
            <div
              key={job.id}
              className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
              onClick={() => handleJobClick(job)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h4 className="text-sm font-medium text-gray-900">
                      {job.job_description?.title || 'Unknown Job'}
                    </h4>
                    {getStatusBadge(job.status)}
                  </div>
                  
                  <div className="mt-1 text-sm text-gray-500">
                    <p>{job.job_description?.company}</p>
                    <p>
                      {job.processed_resumes}/{job.total_resumes} resumes • 
                      Created: {new Date(job.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  {job.status === 'in_progress' && (
                    <div className="mt-2">
                      <div className="bg-gray-200 rounded-full h-2 w-32">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${job.progress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{job.progress}% complete</p>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  {/* Action buttons for different statuses */}
                  {job.status === 'completed' && (
                    <span className="text-xs text-green-600 font-medium">
                      View Results →
                    </span>
                  )}
                  
                  {job.status === 'in_progress' && (
                    <>
                      <button
                        onClick={(e) => handleCancelJob(e, job.id)}
                        disabled={processingJobs.has(job.id)}
                        className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                      >
                        {processingJobs.has(job.id) ? 'Cancelling...' : 'Cancel'}
                      </button>
                      <span className="text-xs text-blue-600 font-medium">
                        Monitor →
                      </span>
                    </>
                  )}
                  
                  {job.status === 'pending' && (
                    <>
                      <button
                        onClick={(e) => handleCancelJob(e, job.id)}
                        disabled={processingJobs.has(job.id)}
                        className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                      >
                        {processingJobs.has(job.id) ? 'Cancelling...' : 'Cancel'}
                      </button>
                      <span className="text-xs text-blue-600 font-medium">
                        Monitor →
                      </span>
                    </>
                  )}
                  
                  {job.status === 'failed' && (
                    <>
                      <button
                        onClick={(e) => handleRetryJob(e, job.id)}
                        disabled={processingJobs.has(job.id)}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                      >
                        {processingJobs.has(job.id) ? 'Retrying...' : 'Retry (Sync)'}
                      </button>
                      <span className="text-xs text-red-600 font-medium">
                        View Error →
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {pagination && pagination.pages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Page {pagination.page} of {pagination.pages}
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!pagination.has_prev}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!pagination.has_next}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RatingJobsList;